import {
  legacySeverityLevelSchema,
  severityFromLevel,
  severityLevel,
  severityLevelSchema,
  severitySchema
} from "./chunk-V4VQB57N.js";

// src/schemas/index.ts
import { z } from "zod";
var factLocationSchema = z.object({
  file: z.string(),
  line: z.number().int().nonnegative(),
  column: z.number().int().nonnegative(),
  endLine: z.number().int().nonnegative().optional(),
  endColumn: z.number().int().nonnegative().optional()
});
var findingReplacePropValueFixSchema = z.object({
  kind: z.literal("replacePropValue"),
  title: z.string(),
  prop: z.string(),
  value: z.unknown(),
  deterministic: z.boolean().optional()
});
var findingReplaceStyleValueFixSchema = z.object({
  kind: z.literal("replaceStyleValue"),
  title: z.string(),
  property: z.string(),
  value: z.string(),
  deterministic: z.boolean().optional()
});
var findingReplaceImportFixSchema = z.object({
  kind: z.literal("replaceImport"),
  title: z.string(),
  from: z.string(),
  to: z.string(),
  deterministic: z.boolean().optional()
});
var findingReplaceComponentFixSchema = z.object({
  kind: z.literal("replaceComponent"),
  title: z.string(),
  from: z.string(),
  to: z.string(),
  deterministic: z.boolean().optional()
});
var findingReplaceClassTokenFixSchema = z.object({
  kind: z.literal("replaceClassToken"),
  title: z.string(),
  from: z.string(),
  to: z.string().nullable(),
  utility: z.string(),
  deterministic: z.boolean().optional()
});
var findingFixSchema = z.discriminatedUnion("kind", [
  findingReplacePropValueFixSchema,
  findingReplaceStyleValueFixSchema,
  findingReplaceImportFixSchema,
  findingReplaceComponentFixSchema,
  findingReplaceClassTokenFixSchema
]);
var factEvidenceSchema = z.object({
  factId: z.string(),
  fact: z.object({
    kind: z.string(),
    location: factLocationSchema.optional()
  }).passthrough()
});
var findingSchema = z.object({
  ruleId: z.string(),
  ruleVersion: z.string(),
  severity: severitySchema,
  level: severityLevelSchema,
  code: z.string().regex(/^FUI\d{4}$/).optional(),
  helpUrl: z.string().url().optional(),
  message: z.string(),
  fingerprint: z.string(),
  location: factLocationSchema,
  evidence: z.array(factEvidenceSchema).min(1),
  fix: findingFixSchema.optional(),
  attributes: z.record(z.unknown()).optional(),
  /**
   * Product area this finding's file resolved to (topology layer). Optional —
   * absent when the repo has no `topology` config. See `topology/resolve-area`.
   */
  area: z.object({
    areaId: z.string(),
    areaName: z.string(),
    criticality: z.enum(["low", "medium", "high", "revenue", "regulated"]),
    owners: z.array(z.string()),
    matchedGlob: z.string()
  }).optional()
});
var violationSchema = z.object({
  nodeId: z.string(),
  nodeType: z.string(),
  rule: z.string(),
  severity: severitySchema,
  level: severityLevelSchema.optional(),
  code: z.string().regex(/^FUI\d{4}$/).optional(),
  helpUrl: z.string().url().optional(),
  message: z.string(),
  path: z.string().optional(),
  suggestion: z.string().optional(),
  prop: z.string().optional(),
  filePath: z.string().optional(),
  line: z.number().int().nonnegative().optional(),
  column: z.number().int().nonnegative().optional(),
  rawValue: z.string().optional(),
  extra: z.record(z.unknown()).optional()
});
var fixSchema = z.object({
  nodeId: z.string(),
  prop: z.string().optional(),
  action: z.enum(["remove", "replace", "add"]),
  value: z.unknown().optional(),
  description: z.string()
});
var validatorResultSchema = z.object({
  validator: z.string(),
  severity: severitySchema,
  passed: z.boolean(),
  violations: z.array(violationSchema),
  findings: z.array(findingSchema).optional(),
  suggestions: z.array(fixSchema).optional()
});
var governanceVerdictMetadataSchema = z.object({
  runner: z.string(),
  policyVersion: z.string().optional(),
  duration: z.number().nonnegative(),
  nodeCount: z.number().int().nonnegative(),
  componentTypes: z.array(z.string())
});
var governanceVerdictSchema = z.object({
  verdict: z.enum(["pass", "warn", "fail"]).optional(),
  passed: z.boolean(),
  score: z.number(),
  results: z.array(validatorResultSchema),
  metadata: governanceVerdictMetadataSchema
});
var suppressionDirectiveSchema = z.object({
  ruleId: z.string().optional(),
  reason: z.string().min(1),
  expiresAt: z.string().datetime().optional(),
  expiresOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  scope: z.enum(["line", "block", "file"]).default("line"),
  code: z.string().regex(/^FUI\d{4}$/).optional()
});
var agentPlanSchema = z.object({
  tool: z.enum([
    "design_system/list_primitives",
    "design_system/conform",
    "design_system/prove_compliant"
  ]),
  args: z.record(z.unknown()),
  confidence: z.number().min(0).max(1)
});
var agentFindingSchema = z.object({
  code: z.string().regex(/^FUI\d{4}$/).optional(),
  ruleId: z.string(),
  filePath: z.string(),
  line: z.number().int().nonnegative(),
  col: z.number().int().nonnegative(),
  severity: severitySchema,
  level: severityLevelSchema,
  message: z.string(),
  evidence: z.array(factEvidenceSchema),
  plan: agentPlanSchema
});
var agentFormatSchema = z.object({
  schemaVersion: z.literal("typestyle.agent.v1"),
  passed: z.boolean(),
  score: z.number(),
  summary: z.string(),
  fix_first: z.array(agentFindingSchema),
  remaining: z.array(agentFindingSchema),
  suppression_template: z.object({
    directive: z.string(),
    requiredFields: z.array(z.string()),
    example: z.string()
  }),
  budget: z.object({
    iteration: z.number().int().nonnegative(),
    maxIterations: z.number().int().positive(),
    scoreDelta: z.number()
  }),
  findings: z.array(findingSchema).optional(),
  suppressions: z.array(suppressionDirectiveSchema).optional()
});
function normalizeSeverity(severity) {
  return severitySchema.safeParse(severity).success ? severity : severityFromLevel(legacySeverityLevelSchema.parse(severity));
}
function normalizeFinding(input) {
  return {
    ...input,
    level: input.level ?? severityLevel(input.severity)
  };
}
function normalizeViolation(input) {
  return {
    ...input,
    level: input.level ?? severityLevel(input.severity)
  };
}

export {
  factLocationSchema,
  findingReplacePropValueFixSchema,
  findingReplaceStyleValueFixSchema,
  findingReplaceImportFixSchema,
  findingReplaceComponentFixSchema,
  findingReplaceClassTokenFixSchema,
  findingFixSchema,
  factEvidenceSchema,
  findingSchema,
  violationSchema,
  fixSchema,
  validatorResultSchema,
  governanceVerdictMetadataSchema,
  governanceVerdictSchema,
  suppressionDirectiveSchema,
  agentPlanSchema,
  agentFindingSchema,
  agentFormatSchema,
  normalizeSeverity,
  normalizeFinding,
  normalizeViolation
};
//# sourceMappingURL=chunk-R3X7UBHU.js.map